import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SourceFormFields } from '../types/sources.form-fields';
import { SocialNetworkEnum } from '../types/sources.types';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';

const NETWORK_OPTIONS = [
  { label: 'Facebook', value: String(SocialNetworkEnum.Facebook) },
  { label: 'Instagram', value: String(SocialNetworkEnum.Instagram) },
  { label: 'X', value: String(SocialNetworkEnum.X) },
];

/**
 * Dynamic form fields components
 * Each component renders fields specific to a source type
 */

/**
 * Fields for Medios Abiertos source type
 * Requires: baseUrl
 */
export const OpenMediaFields: React.FC = () => {
  const { control, formState } = useFormContext<SourceFormFields>();

  return (
    <FormField
      control={control}
      name="baseUrl"
      render={({ field }) => (
        <FormItem className="w-full">
          <FormControl>
            <Input
              label="URL base o endpoint RSS/HTTP"
              placeholder="https://ejemplo.com/rss"
              type="url"
              {...field}
              error={formState.errors.baseUrl?.message}
              data-testid="create-source-baseurl-input"
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

/**
 * Fields for Medios Suscripción source type
 * Requires: baseUrl, username, password
 */
export const SubscriptionMediaFields: React.FC = () => {
  const { control, formState } = useFormContext<SourceFormFields>();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="grid grid-cols-1 gap-[1.23rem]">
      <FormField
        control={control}
        name="baseUrl"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <Input
                label="URL de acceso o login"
                placeholder="https://ejemplo.com/login"
                type="url"
                {...field}
                error={formState.errors.baseUrl?.message}
                data-testid="create-source-subscription-baseurl-input"
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="username"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <Input
                label="Usuario / correo de sesión"
                placeholder="usuario@ejemplo.com"
                autoComplete="off"
                {...field}
                error={formState.errors.username?.message}
                data-testid="create-source-username-input"
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="password"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <Input
                label="Contraseña de sesión"
                placeholder="••••••••"
                type={showPassword ? 'text' : 'password'}
                autoComplete="off"
                iconEnd={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-primary hover:text-light transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
                {...field}
                error={formState.errors.password?.message}
                data-testid="create-source-password-input"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};

/**
 * Fields for Redes Sociales source type
 * Requires: network (the social platform the source represents)
 */
export const SocialMediaFields: React.FC = () => {
  const { control, formState } = useFormContext<SourceFormFields>();

  return (
    <FormField
      control={control}
      name="network"
      render={({ field }) => (
        <FormItem className="w-full">
          <FormControl>
            <SelectInput
              label="Red social"
              value={field.value !== undefined ? String(field.value) : ''}
              onValueChange={(value) => {
                // Radix Select fires onValueChange("") from its hidden native <select> when the
                // controlled value changes before the native options are mounted. Number("") === 0,
                // which would corrupt the field to Facebook. Ignore the empty string.
                if (value === '') return;
                field.onChange(Number(value));
              }}
              placeholder="Seleccionar red social"
              error={formState.errors.network?.message}
              data-testid="create-source-network-select"
            >
              {NETWORK_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectInput>
          </FormControl>
        </FormItem>
      )}
    />
  );
};

// TODO: not implemented yet
// /**
//  * Fields for Google Trends source type
//  * Requires: keywords (array with at least 1 item)
//  * No URL or credentials needed
//  */
// export const GoogleTrendsFields: React.FC = () => {
//   const { watch, setValue, formState } = useFormContext<SourceFormFields>();
//   const keywords = watch('keywords') || [];
//   const [inputValue, setInputValue] = useState('');
//
//   const handleAddKeyword = () => {
//     const trimmed = inputValue.trim();
//     if (trimmed && !keywords.includes(trimmed)) {
//       setValue('keywords', [...keywords, trimmed]);
//       setInputValue('');
//     }
//   };
//
//   const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     if (e.key === 'Enter') {
//       e.preventDefault();
//       handleAddKeyword();
//     }
//   };
//
//   const handleRemoveKeyword = (tag: string) => {
//     setValue(
//       'keywords',
//       keywords.filter((k) => k !== tag),
//     );
//   };
//
//   return (
//     <div className="flex flex-col gap-[1.23rem]">
//       <div>
//         <label className="text-sm font-medium text-primary mb-2 block">
//           Términos o palabras clave de seguimiento
//         </label>
//         <Input
//           placeholder="Ingresar término y presionar Enter"
//           value={inputValue}
//           onChange={(e) => setInputValue(e.target.value)}
//           onKeyDown={handleKeyDown}
//           error={formState.errors.keywords?.message}
//           data-testid="create-source-keywords-input"
//         />
//         <p className="text-xs text-primary mt-1">Presiona Enter para agregar cada término</p>
//       </div>
//
//       {keywords.length > 0 && <TagsSection tags={keywords} onRemove={handleRemoveKeyword} />}
//     </div>
//   );
// };

/**
 * Fields for Comunicados source type
 * Requires: baseUrl
 */
export const CommunicatesFields: React.FC = () => {
  const { control, formState } = useFormContext<SourceFormFields>();

  return (
    <FormField
      control={control}
      name="baseUrl"
      render={({ field }) => (
        <FormItem className="w-full">
          <FormControl>
            <Input
              label="URL base o endpoint RSS/HTTP"
              placeholder="https://ejemplo.com/comunicados"
              type="url"
              {...field}
              error={formState.errors.baseUrl?.message}
              data-testid="create-source-comunicados-baseurl-input"
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};
